import { Component, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { Permissao, PermissoesApiService } from '@cei-ufg/ngx-cei-seguranca';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import {
  CeiAcaoLinhaTabela,
  CeiButtonComponent,
  CeiConfirmDialogService,
  CeiScrollableActionTableComponent,
  CeiScrollableActionTablePageFetcher,
  CeiSnackBarService,
  CeiTableColumn,
  CeiTablePage,
} from '@cei-ufg/ngx-arquitetura-cei-frontend';
import { notifyAdminFormSuccess } from '../../shared/admin-form.utils';
import { notifyAdminListError } from '../../shared/admin-list.utils';
import {
  GerenciadorLarguraColunaId,
  atualizarColunasIdAoCarregarPagina,
} from '../../shared/admin-list-columns';
import { ADMIN_CARREGAMENTO_TABELA } from '../../shared/admin-loading.config';
import { normalizarPaginaApi, paraRequisicaoPaginacaoOpenApi } from '../../shared/admin-paginacao.utils';
import { firstValueFrom, map } from 'rxjs';

/** Listagem de permissões com paginação por scroll e ações declarativas CEI. */
@Component({
  selector: 'cei-admin-permissao-list',
  standalone: true,
  imports: [CommonModule, RouterModule, CeiButtonComponent, CeiScrollableActionTableComponent],
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
})
export class AdminPermissaoListComponent {
  @ViewChild('tabela') private tabela?: CeiScrollableActionTableComponent<Permissao>;

  private readonly colunasBase: CeiTableColumn<Permissao>[] = [
    { key: 'id', label: 'ID', property: 'id', sortable: true },
    { key: 'name', label: 'Nome', property: 'name', sortable: true },
    { key: 'codename', label: 'Codename', property: 'codename', sortable: true },
  ];

  private readonly larguraColunaId = new GerenciadorLarguraColunaId();

  protected columns = this.larguraColunaId.aplicar(this.colunasBase);

  protected readonly carregamentoTabela = ADMIN_CARREGAMENTO_TABELA;

  protected readonly autoLoad = isPlatformBrowser(inject(PLATFORM_ID));

  protected readonly acoesLinha: CeiAcaoLinhaTabela<Permissao>[] = [
    {
      id: 'editar',
      rotulo: 'Editar',
      icone: 'editar',
      rota: (linha) => ['/admin/permissoes/atualizar', linha.id],
    },
    {
      id: 'excluir',
      rotulo: 'Excluir',
      icone: 'excluir',
      executar: (linha) => this.excluirPermissao(linha),
    },
  ];

  protected readonly buscarPagina: CeiScrollableActionTablePageFetcher<Permissao> = (request) =>
    this.permissoesApi
      .permissaoList(paraRequisicaoPaginacaoOpenApi(request), 'body', false, { transferCache: false })
      .pipe(map((resposta) => normalizarPaginaApi(resposta)));

  private readonly permissoesApi = inject(PermissoesApiService);
  private readonly confirmDialog = inject(CeiConfirmDialogService);
  private readonly snackBar = inject(CeiSnackBarService);
  private readonly router = inject(Router);

  /** Navega para detalhes ao selecionar a linha na pílula. */
  protected aoSelecionarLinha(permissao: Permissao): void {
    void this.router.navigate(['/admin/permissoes/detalhe', permissao.id]);
  }

  /** Exibe erro de carregamento remoto emitido pela tabela. */
  protected aoErroCarregamento(erro: unknown): void {
    notifyAdminListError(this.snackBar, 'Erro ao carregar permissões.', erro);
  }

  protected aoCarregarPagina(pagina: CeiTablePage<Permissao>): void {
    const colunas = atualizarColunasIdAoCarregarPagina(this.larguraColunaId, this.colunasBase, pagina);
    if (colunas) {
      this.columns = colunas;
    }
  }

  /** Confirma e remove permissão; recarrega a listagem após sucesso. */
  private excluirPermissao(permissao: Permissao): void {
    this.confirmDialog
      .confirm({
        title: 'Excluir permissão',
        content: `Deseja realmente excluir a permissão "${permissao.name}"?`,
        yesLabel: 'Sim',
        noLabel: 'Não',
      })
      .subscribe(async (confirmed) => {
        if (!confirmed) {
          return;
        }

        try {
          await firstValueFrom(this.permissoesApi.permissaoDestroy({ id: Number(permissao.id) }));
          notifyAdminFormSuccess(this.snackBar, 'Permissão excluída com sucesso!');
          this.tabela?.recarregar();
        } catch (err) {
          notifyAdminListError(this.snackBar, 'Erro ao excluir permissão.', err);
        }
      });
  }
}
